import { useQueryClient } from '@tanstack/react-query';
import { Phone, Plus, Search } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { type LocalContact, useCreateAccountFlow } from '@/context/create-account-flow';
import { useCanSearchGroupContacts } from '@/hooks/use-can-search-group-contacts';
import { useToast } from '@/hooks/use-toast';
import { mergeFiscalContacts } from '@/lib/merge-fiscal-contacts';
import { SearchContactDialog } from '../search-contact-dialog';
import { ContactCard } from './contact-card';
import { ContactFormDialog } from './contact-form-dialog';

export function StepContacts() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const {
    account,
    addresses: _addresses,
    contacts,
    economicGroup,
    createContactLocally,
    updateContactLocally,
    deleteContactLocally,
    resetFlow,
    createEntitiesApi,
  } = useCreateAccountFlow();

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [searchContactOpen, setSearchContactOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<LocalContact | undefined>();
  const [isCreatingEntities, setIsCreatingEntities] = useState(false);
  const canSearchGroupContacts = useCanSearchGroupContacts(
    economicGroup?.existingGroupId,
    account?.id,
    account?.document?.type,
  );

  // Check if account exists
  if (!account?.document?.value) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Conta não encontrada</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground mb-4">
            Os dados da conta não foram encontrados. Isso pode acontecer se você navegou diretamente
            para esta página ou se os dados foram perdidos.
          </p>
          <Button onClick={() => navigate('/commercial/accounts/create?step=account')}>
            Criar nova conta
          </Button>
        </CardContent>
      </Card>
    );
  }

  const mergedContacts = mergeFiscalContacts(contacts);

  const handleCreateContact = (data: LocalContact) => {
    createContactLocally(data);
    toast({
      variant: 'success',
      title: 'Sucesso!',
      description: 'Contato adicionado com sucesso!',
    });
  };

  const handleUpdateContact = (data: LocalContact) => {
    if (selectedContact) {
      updateContactLocally(selectedContact.id, data);
      toast({
        variant: 'success',
        title: 'Sucesso!',
        description: 'Contato atualizado com sucesso!',
      });
    }
  };

  const handleDuplicateContact = (data: LocalContact) => {
    createContactLocally(data);
    toast({
      variant: 'success',
      title: 'Sucesso!',
      description: 'Contato duplicado com sucesso!',
    });
  };

  const handleDeleteContact = () => {
    if (selectedContact) {
      deleteContactLocally(selectedContact.id);
      setDeleteDialogOpen(false);
      setSelectedContact(undefined);
      toast({
        variant: 'success',
        title: 'Sucesso!',
        description: 'Contato excluído com sucesso!',
      });
    }
  };

  const handleCancel = () => {
    resetFlow();
    toast({
      variant: 'info',
      title: 'Operação cancelada',
      description:
        'O fluxo de criação foi cancelado e você foi redirecionado para a página inicial.',
    });
    navigate('/commercial/accounts');
  };

  const handleFinalizarSalvar = async () => {
    setIsCreatingEntities(true);

    try {
      const result = await createEntitiesApi();

      if (result.success && result.accountId) {
        toast({
          variant: 'success',
          title: 'Sucesso!',
          description: 'Conta e dados relacionados criados com sucesso!',
        });

        // Invalidate queries
        await queryClient.invalidateQueries({
          queryKey: ['accounts'],
        });
        await queryClient.invalidateQueries({
          queryKey: ['findOneAccount', result.accountId],
        });

        resetFlow();

        navigate('/commercial/accounts');
      } else {
        toast({
          title: 'Erro',
          description: result.error || 'Erro ao criar entidades',
          variant: 'destructive',
        });
        setIsCreatingEntities(false);
      }
    } catch (error) {
      console.error('Error in handleFinalizarSalvar:', error);
      toast({
        title: 'Erro',
        description: 'Erro inesperado ao finalizar criação',
        variant: 'destructive',
      });
      setIsCreatingEntities(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="w-full">
        <CardHeader className="pb-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Phone className="h-5 w-5 text-primary" />
              Contatos
              <Badge variant="secondary" className="text-xs">
                {contacts?.length || 0}
              </Badge>
            </CardTitle>
            <div className="flex gap-2">
              {canSearchGroupContacts && (
                <Button variant="outline" onClick={() => setSearchContactOpen(true)}>
                  <Search className="h-4 w-4 mr-2" />
                  Buscar Contato
                </Button>
              )}
              <Button variant="outline" onClick={() => setCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Adicionar
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {contacts?.length > 0 ? (
            mergedContacts.map((contact, idx) => (
              <ContactCard
                key={contact.id ?? idx}
                contact={contact}
                onEdit={() => {
                  setSelectedContact(contact);
                  setEditDialogOpen(true);
                }}
                onDelete={() => {
                  setSelectedContact(contact);
                  setDeleteDialogOpen(true);
                }}
                onDuplicate={
                  contact.contactFor?.includes('Fiscal')
                    ? () => {
                        setSelectedContact(contact);
                        setDuplicateDialogOpen(true);
                      }
                    : undefined
                }
              />
            ))
          ) : (
            <div className="space-y-4">
              <p className="text-muted-foreground">Nenhum contato cadastrado.</p>
              <Button onClick={() => setCreateDialogOpen(true)} variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                Adicionar Contato
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Footer Buttons */}
      <div className="flex gap-4 items-end justify-end mt-4">
        <Button onClick={handleCancel} type="button" variant="ghost">
          Cancelar
        </Button>
        <Button
          onClick={handleFinalizarSalvar}
          className="bg-black text-white hover:bg-gray-800"
          disabled={isCreatingEntities}
        >
          {isCreatingEntities ? 'Criando...' : 'Finalizar e Salvar Conta'}
        </Button>
      </div>

      {/* Create Contact Dialog */}
      <ContactFormDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateContact}
        mode="create"
      />

      {/* Edit Contact Dialog */}
      <ContactFormDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedContact(undefined);
        }}
        onSubmit={handleUpdateContact}
        contact={selectedContact}
        mode="edit"
      />

      {/* Duplicate Contact Dialog */}
      <ContactFormDialog
        open={duplicateDialogOpen}
        onClose={() => {
          setDuplicateDialogOpen(false);
          setSelectedContact(undefined);
        }}
        onSubmit={handleDuplicateContact}
        contact={selectedContact}
        mode="create"
        duplicateMode
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Contato</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este contato? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedContact(undefined)}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteContact}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Search Contact from Economic Group */}
      {economicGroup?.existingGroupId && (
        <SearchContactDialog
          open={searchContactOpen}
          onClose={() => setSearchContactOpen(false)}
          economicGroupId={economicGroup.existingGroupId}
          currentAccountId=""
          existingContacts={contacts}
          onImport={(importedContacts) => {
            for (const contact of importedContacts) {
              createContactLocally({
                id: crypto.randomUUID(),
                name: contact.name,
                contractEnabled: contact.contractEnabled,
                positionOrRelation: contact.positionOrRelation,
                area: contact.area || contact.department || '',
                cpf: contact.cpf || '',
                rg: contact.rg || '',
                taxId: contact.taxId,
                contactFor: contact.contactFor,
                contactItems: contact.contactItems,
              });
            }
            toast({
              variant: 'success',
              title: 'Sucesso!',
              description: `${importedContacts.length} contato(s) importado(s).`,
            });
          }}
        />
      )}
    </div>
  );
}
